import { useState, type ComponentType, type SVGProps } from 'react'
import { cn } from '@/lib/utils'
import { Button, IconButton, TextField, ToggleGroup, Tooltip, type ToggleGroupItem } from '@/components/play'
import {
  AlignLeft,
  Badge,
  BadgeCheck,
  ChevronDown,
  ChevronUp,
  CircleOff,
  EyeOff,
  Grid3X3,
  Heading,
  Heart,
  LayoutGrid,
  ListOrdered,
  ListVideo,
  Map,
  Maximize,
  PanelBottom,
  PanelTop,
  PictureInPicture,
  Play,
  Search,
  X,
} from 'lucide-react'
import {
  DESTINATION_COMPONENT_DEFINITIONS,
  type DestinationComponentCategory,
} from '@/components/event-viewer/destination'

export interface PrimitiveDefinition {
  id: string
  name: string
  description: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  category: DestinationComponentCategory
  example: string
}

const ICONS: Record<
  string,
  ComponentType<SVGProps<SVGSVGElement>>
> = {
  'panel-top': PanelTop,
  play: Play,
  heading: Heading,
  text: AlignLeft,
  'badge-check': BadgeCheck,
  heart: Heart,
  'list-video': ListVideo,
  badge: Badge,
  'list-ordered': ListOrdered,
  map: Map,
  'panel-bottom': PanelBottom,
  'eye-off': EyeOff,
  maximize: Maximize,
  x: X,
  'picture-in-picture': PictureInPicture,
  'circle-off': CircleOff,
  'layout-grid': LayoutGrid,
}

export const PRIMITIVES_CATALOG: PrimitiveDefinition[] =
  DESTINATION_COMPONENT_DEFINITIONS.map((definition) => ({
    id: definition.name,
    name: definition.editor.displayName,
    description: definition.editor.description,
    icon: ICONS[definition.editor.icon] ?? Grid3X3,
    category: definition.category,
    example: `Add the ${definition.editor.displayName} component. ${definition.ai.useWhen}`,
  }))

const CATEGORY_LABELS: Record<DestinationComponentCategory, string> = {
  media: 'Media',
  event: 'Event',
  tracking: 'Tracking',
  results: 'Results',
  interaction: 'Interaction',
  overlay: 'Overlay',
}

const CATEGORIES = Object.keys(CATEGORY_LABELS) as DestinationComponentCategory[]

type CategoryFilter = DestinationComponentCategory | 'all'

// “All” stays as an option so the unfiltered state reads as a pressed choice; pressing the chosen category also
// returns to All, as the chips did before.
const CATEGORY_ITEMS: ToggleGroupItem<CategoryFilter>[] = [
  { value: 'all', label: 'All' },
  ...CATEGORIES.map((category) => ({ value: category, label: CATEGORY_LABELS[category] })),
]

interface PrimitivesPaletteProps {
  onSelect: (primitive: PrimitiveDefinition) => void
  className?: string
  collapsed?: boolean
  onToggleCollapse?: () => void
}

export function PrimitivesPalette({
  onSelect,
  className,
  collapsed = false,
  onToggleCollapse,
}: PrimitivesPaletteProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeCategory, setActiveCategory] =
    useState<DestinationComponentCategory | null>(null)

  const query = searchQuery.trim().toLowerCase()
  const filteredPrimitives = PRIMITIVES_CATALOG.filter((primitive) => {
    const matchesSearch =
      !query ||
      primitive.name.toLowerCase().includes(query) ||
      primitive.description.toLowerCase().includes(query)
    return (
      matchesSearch &&
      (!activeCategory || primitive.category === activeCategory)
    )
  })

  if (collapsed) {
    return (
      <div className={cn('border-t border-neutral-4 bg-neutral-2 px-s py-xxs', className)}>
        {/* Named by what it shows, so voice control matches the visible label. */}
        <Button variant="ghost" className="w-full justify-between" aria-expanded={false} onClick={onToggleCollapse}>
          <span className="flex items-center gap-xs">
            <Grid3X3 aria-hidden="true" />
            Components
            <span className="text-neutral-7">({PRIMITIVES_CATALOG.length})</span>
          </span>
          <ChevronUp aria-hidden="true" />
        </Button>
      </div>
    )
  }

  return (
    <div className={cn('border-t border-neutral-4 bg-neutral-2', className)}>
      <div className="flex items-center justify-between border-b border-neutral-4 px-4 py-2">
        <div className="flex items-center gap-2">
          <Grid3X3 className="h-4 w-4 text-neutral-7" />
          <span className="text-sm font-medium text-neutral-10">Components</span>
        </div>
        <div className="flex items-center gap-2">
          <TextField
            label="Search destination components"
            hideLabel
            size="compact"
            icon={<Search />}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search…"
            className="w-36"
          />
          {onToggleCollapse && (
            <IconButton
              label="Collapse destination component catalog"
              variant="ghost"
              size="compact"
              onClick={onToggleCollapse}
            >
              <ChevronDown />
            </IconButton>
          )}
        </div>
      </div>

      <div className="border-b border-neutral-4 px-4 py-2">
        <ToggleGroup
          label="Component category"
          hideLabel
          allowNone
          items={CATEGORY_ITEMS}
          value={activeCategory ?? 'all'}
          onValueChange={(value) => setActiveCategory(value === 'all' ? null : value)}
        />
      </div>

      <div className="max-h-[220px] overflow-y-auto p-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
          {filteredPrimitives.map((primitive) => (
            <PrimitiveCard
              key={primitive.id}
              primitive={primitive}
              onClick={() => onSelect(primitive)}
            />
          ))}
        </div>
        {filteredPrimitives.length === 0 && (
          <p className="py-6 text-center text-sm text-neutral-7">
            No components found
          </p>
        )}
      </div>
    </div>
  )
}

function PrimitiveCard({
  primitive,
  onClick,
}: {
  primitive: PrimitiveDefinition
  onClick: () => void
}) {
  const Icon = primitive.icon
  return (
    <Tooltip content={primitive.description}>
      <Button variant="secondary" size="compact" onClick={onClick} className="w-full min-w-0 justify-start">
        <Icon aria-hidden="true" />
        <span className="truncate">{primitive.name}</span>
      </Button>
    </Tooltip>
  )
}

export default PrimitivesPalette
